import * as THREE from 'three';

export interface OrbitTargetFollowerControls {
  readonly target: THREE.Vector3;
  update(): void;
  addEventListener?(type: 'start' | 'end', listener: () => void): void;
  removeEventListener?(type: 'start' | 'end', listener: () => void): void;
}

export interface OrbitTargetFollowerOptions {
  readonly controls: OrbitTargetFollowerControls;
  readonly domElement?: EventTarget;
  readonly durationMs?: number;
  readonly interactionResumeDelayMs?: number;
  readonly isUserInteractionTarget?: (target: EventTarget | null) => boolean;
}

export interface OrbitTargetFollower {
  follow(target: THREE.Vector3): void;
  snapTo(target: THREE.Vector3): void;
  update(deltaSeconds: number): void;
  notifyUserInteractionStart(): void;
  notifyUserInteractionEnd(): void;
  dispose(): void;
}

const DEFAULT_FOLLOW_DURATION_MS = 600;
const DEFAULT_INTERACTION_RESUME_DELAY_MS = 180;
const TARGET_EPSILON_SQ = 0.000001;

export function createOrbitTargetFollower(
  options: OrbitTargetFollowerOptions,
): OrbitTargetFollower {
  const {
    controls,
    domElement,
    durationMs = DEFAULT_FOLLOW_DURATION_MS,
    interactionResumeDelayMs = DEFAULT_INTERACTION_RESUME_DELAY_MS,
    isUserInteractionTarget = () => true,
  } = options;

  const start = new THREE.Vector3();
  const destination = new THREE.Vector3();
  let elapsedMs = 0;
  let hasDestination = false;
  let interactionActive = false;
  let resumeDelayRemainingMs = 0;
  let rebaseAfterPause = false;
  let disposed = false;

  function rebaseFollowStart(): void {
    if (!hasDestination) return;
    start.copy(controls.target);
    elapsedMs = 0;
    rebaseAfterPause = false;
  }

  function pauseForInteraction(): void {
    if (disposed) return;
    interactionActive = true;
    resumeDelayRemainingMs = interactionResumeDelayMs;
    rebaseAfterPause = true;
  }

  function resumeAfterInteraction(): void {
    if (disposed) return;
    interactionActive = false;
    resumeDelayRemainingMs = interactionResumeDelayMs;
  }

  function handlePointerDown(event: Event): void {
    if (!isUserInteractionTarget(event.target)) return;
    pauseForInteraction();
  }

  function handlePointerEnd(): void {
    resumeAfterInteraction();
  }

  controls.addEventListener?.('start', pauseForInteraction);
  controls.addEventListener?.('end', resumeAfterInteraction);
  domElement?.addEventListener('pointerdown', handlePointerDown, { capture: true });
  domElement?.addEventListener('pointerup', handlePointerEnd, { capture: true });
  domElement?.addEventListener('pointercancel', handlePointerEnd, { capture: true });

  function follow(target: THREE.Vector3): void {
    if (disposed) return;
    if (!hasDestination && controls.target.distanceToSquared(target) <= TARGET_EPSILON_SQ) return;
    if (hasDestination && destination.distanceToSquared(target) <= TARGET_EPSILON_SQ) return;
    start.copy(controls.target);
    destination.copy(target);
    elapsedMs = 0;
    hasDestination = true;
    rebaseAfterPause = false;
  }

  function snapTo(target: THREE.Vector3): void {
    if (disposed) return;
    controls.target.copy(target);
    controls.update();
    hasDestination = false;
    elapsedMs = 0;
    interactionActive = false;
    resumeDelayRemainingMs = 0;
    rebaseAfterPause = false;
  }

  function update(deltaSeconds: number): void {
    if (disposed || !hasDestination) return;

    const deltaMs = Math.max(0, deltaSeconds * 1000);
    if (interactionActive || resumeDelayRemainingMs > 0) {
      if (!interactionActive) {
        resumeDelayRemainingMs = Math.max(0, resumeDelayRemainingMs - deltaMs);
      }
      return;
    }

    if (rebaseAfterPause) {
      rebaseFollowStart();
    }

    elapsedMs += deltaMs;
    const t = durationMs <= 0 ? 1 : Math.min(elapsedMs / durationMs, 1);
    controls.target.lerpVectors(start, destination, t);
    controls.update();

    if (t >= 1) {
      controls.target.copy(destination);
      hasDestination = false;
      rebaseAfterPause = false;
    }
  }

  function dispose(): void {
    if (disposed) return;
    disposed = true;
    controls.removeEventListener?.('start', pauseForInteraction);
    controls.removeEventListener?.('end', resumeAfterInteraction);
    domElement?.removeEventListener('pointerdown', handlePointerDown, { capture: true });
    domElement?.removeEventListener('pointerup', handlePointerEnd, { capture: true });
    domElement?.removeEventListener('pointercancel', handlePointerEnd, { capture: true });
  }

  return {
    follow,
    snapTo,
    update,
    notifyUserInteractionStart: pauseForInteraction,
    notifyUserInteractionEnd: resumeAfterInteraction,
    dispose,
  };
}
